import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import ParticleBackground from "@/components/ParticleBackground";
import AIGrid from "@/components/AIGrid";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Code, Cloud, Users, TrendingUp, Rocket, Heart, Zap, Globe } from "lucide-react";
import { motion } from "framer-motion";
import teamImage from "@/assets/team-collaboration.jpg";
import { useTranslation } from 'react-i18next';

const Careers = () => {
  const { t } = useTranslation();
  
  const roles = [
    { title: t('careers.role_ai'), department: t('careers.dept_engineering'), location: t('careers.location_hybrid'), type: t('careers.type_fulltime') },
    { title: t('careers.role_devops'), department: t('careers.dept_infrastructure'), location: t('careers.location_remote'), type: t('careers.type_fulltime') },
    { title: t('careers.role_sales'), department: t('careers.dept_sales'), location: t('careers.location_cologne'), type: t('careers.type_fulltime') },
    { title: t('careers.role_marketing'), department: t('careers.dept_marketing'), location: t('careers.location_hybrid'), type: t('careers.type_fulltime') },
  ];

  const benefits = [
    { 
      icon: Code, 
      title: t('careers.tech_title'), 
      description: t('careers.tech_desc'),
      gradient: "from-gradient-pink/20 to-gradient-purple/20"
    },
    { 
      icon: Cloud, 
      title: t('careers.flex_title'), 
      description: t('careers.flex_desc'),
      gradient: "from-gradient-purple/20 to-gradient-blue/20"
    },
    { 
      icon: Users, 
      title: t('careers.team_title'), 
      description: t('careers.team_desc'),
      gradient: "from-gradient-blue/20 to-gradient-cyan/20"
    },
    { 
      icon: TrendingUp, 
      title: t('careers.growth_title'), 
      description: t('careers.growth_desc'),
      gradient: "from-gradient-cyan/20 to-gradient-pink/20"
    },
  ];

  const perks = [
    { icon: Rocket, text: t('careers.perk_salary') },
    { icon: Heart, text: t('careers.perk_health') },
    { icon: Zap, text: t('careers.perk_learning') },
    { icon: Globe, text: t('careers.perk_remote') },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background relative overflow-hidden">
      <ParticleBackground />
      <AIGrid />
      <Navigation />
      
      <main className="flex-grow pt-32 pb-20 relative z-10">
        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-shadow-glow">
              {t('careers.title')}
            </h1>
            <p className="text-xl text-foreground/80 max-w-3xl mx-auto">
              {t('careers.subtitle')}
            </p>
          </motion.div>

          <div className="relative rounded-2xl overflow-hidden mb-16">
            <img 
              src={teamImage} 
              alt="Team collaboration" 
              className="w-full h-96 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-transparent flex items-end justify-center p-12">
              <div className="text-center">
                <h2 className="text-3xl font-bold mb-4">{t('careers.hero_title')}</h2>
                <p className="text-lg text-foreground/80">{t('careers.hero_subtitle')}</p>
              </div>
            </div>
          </div>

          <div className="mb-20">
            <h2 className="text-3xl font-bold mb-8 text-center">{t('careers.why_title')}</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className={`p-6 card-dark text-center hover:glow-effect transition-all duration-300 h-full bg-gradient-to-br ${benefit.gradient}`}>
                    <div className="w-12 h-12 rounded-lg gradient-brand flex items-center justify-center mx-auto mb-4">
                      <benefit.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="font-bold mb-2">{benefit.title}</h3>
                    <p className="text-sm text-foreground/70">{benefit.description}</p>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="mb-20">
            <h2 className="text-3xl font-bold mb-8 text-center">{t('careers.perks_title')}</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-4xl mx-auto">
              {perks.map((perk, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center gap-3 card-dark p-4 rounded-lg"
                >
                  <perk.icon className="w-6 h-6 text-accent flex-shrink-0" />
                  <span className="text-sm font-medium">{perk.text}</span>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8">{t('careers.positions_title')}</h2>
            <div className="space-y-4">
              {roles.map((role, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="p-6 card-dark gradient-border hover:glow-effect transition-all duration-300">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div>
                        <h3 className="text-xl font-bold mb-2">{role.title}</h3>
                        <div className="flex flex-wrap gap-3 text-sm text-foreground/70">
                          <span className="px-3 py-1 rounded-full bg-accent/20 text-accent">{role.department}</span>
                          <span className="px-3 py-1 rounded-full bg-foreground/10">{role.location}</span>
                          <span className="px-3 py-1 rounded-full bg-foreground/10">{role.type}</span>
                        </div>
                      </div>
                      <Button className="gradient-brand text-white hover:opacity-90 w-full md:w-auto">
                        {t('careers.apply_button')}
                      </Button>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>

            <Card className="mt-12 p-10 card-dark gradient-border text-center glow-effect">
              <h3 className="text-2xl font-bold mb-4">{t('careers.no_role_title')}</h3>
              <p className="text-foreground/70 mb-6">
                {t('careers.no_role_desc')}
              </p>
              <Button className="gradient-brand text-white hover:opacity-90">
                {t('careers.send_application')}
              </Button>
            </Card>
          </div>

          <div className="mt-20 text-center">
            <p className="text-foreground/70 mb-2">📍 {t('careers.location_footer')} 🇩🇪</p>
            <p className="text-foreground/70">{t('careers.remote_info')}</p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Careers;
